import { faBullhorn } from '@fortawesome/pro-regular-svg-icons';
import { BoxV2 as Box, Button, Flex, useHasPermission } from 'portal-commons';
import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  CampaignDetail,
  UseCase,
  campaignController,
  enumController,
} from '@/apis/core';
import { remapTCRStatus } from '@/applications/CspPortal/Campaigns/utils';
import { CAMPAIGN_REGISTRATION, MY_CAMPAIGNS_PATH } from '@/constants/paths';
import { toastFlashMessage } from '@/utils';
import { convertTimeWithTimezone } from '@/utils/time';
import InfoItem from './InfoItem';
import InfoItemLayout from './InfoItemLayout';
import RcsSectionBlock from './RcsSectionBlock';
import StatusIndicator from './StatusIndicator';
import TenDlcCampaignFallbackEditModal, {
  ModalType,
} from './TenDlcCampaignFallbackEditModal';

interface TenDlcCampaignFallbackSectionProps {
  rcsCampaignUid: string;
  brandUid: string;
  brandName: string;
  disableActions: boolean;
  fallbackCampaignUid?: string;
  onChange: (fallbackCampaignUid: string | null) => void;
}

const TenDlcCampaignFallbackSection: FC<TenDlcCampaignFallbackSectionProps> = ({
  rcsCampaignUid,
  brandUid,
  brandName,
  fallbackCampaignUid,
  disableActions,
  onChange,
}) => {
  const [fallbackCampaign, setFallbackCampaign] = useState<CampaignDetail>();
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [processing, setProcessing] = useState(false);
  const [usecases, setUseCases] = useState<UseCase[]>([]);
  const authorized = useHasPermission('rcsCampaignDetail.editFallbackCampaign');
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    enumController.getUseCases({ channelId: '10DLC' }).then((data) => {
      setUseCases(data);
    });
  }, []);

  useEffect(() => {
    if (fallbackCampaignUid) {
      campaignController.getCampaignDetail(fallbackCampaignUid).then((data) => {
        if (data) {
          setFallbackCampaign(data);
        }
      });
    }

    return () => setFallbackCampaign(undefined);
  }, [fallbackCampaignUid]);

  const useCaseDisplayName = useMemo(() => {
    return (
      usecases.find((uc) => uc.id === fallbackCampaign?.usecase)?.displayName ||
      fallbackCampaign?.usecase
    );
  }, [usecases, fallbackCampaign?.usecase]);

  const ActionButtons = () => {
    return (
      <Flex sx={{ gap: 's' }}>
        {fallbackCampaignUid ? (
          <>
            <Button
              size="small"
              shape="square"
              variant="outline"
              disabled={disableActions}
              data-testid="removeFallbackCampaignButton"
              onClick={() => setModalType(ModalType.Remove)}
            >
              Remove
            </Button>
            <Button
              size="small"
              shape="square"
              disabled={disableActions}
              data-testid="updateFallbackCampaignButton"
              onClick={() => setModalType(ModalType.Update)}
            >
              Update
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              shape="square"
              variant="outline"
              disabled={disableActions}
              data-testid="createFallbackCampaignButton"
              onClick={() => {
                history.push(CAMPAIGN_REGISTRATION, {
                  goBackPage: `${location.pathname}${location.search}`,
                  brandUid,
                  brandName,
                  rcsFallback: {
                    rcsCampaignUid,
                  },
                });
              }}
            >
              Create Campaign
            </Button>
            <Button
              size="small"
              shape="square"
              disabled={disableActions}
              data-testid="selectFallbackCampaignButton"
              onClick={() => setModalType(ModalType.Select)}
            >
              Select Fallback
            </Button>
          </>
        )}
      </Flex>
    );
  };

  const closeModal = () => {
    setModalType(null);
  };

  const handleFallbackAction = async (
    apiCall: () => Promise<any>,
    onSuccess: () => void
  ) => {
    setProcessing(true);
    const result = await apiCall();
    setProcessing(false);
    if (result) {
      onSuccess();
      closeModal();
    }
  };

  const handleRemoveFallback = () => {
    return handleFallbackAction(
      () => campaignController.deleteRcsFallbackCampaign(rcsCampaignUid),
      () => {
        toastFlashMessage(
          `${fallbackCampaignUid} successfully removed as fallback campaign`,
          'success'
        );
        onChange(null);
      }
    );
  };

  const updateFallback = (
    newFallbackCampaignUid: string,
    successMessage: string
  ) => {
    const apiCall = () =>
      campaignController.updateCampaign(rcsCampaignUid, {
        fallbackCampaignUid: newFallbackCampaignUid,
      });
    const onSuccess = () => {
      toastFlashMessage(successMessage, 'success');
      onChange(newFallbackCampaignUid);
    };
    return handleFallbackAction(apiCall, onSuccess);
  };

  const handleSelectFallback = (newFallbackCampaignUid: string) => {
    return updateFallback(
      newFallbackCampaignUid,
      `${newFallbackCampaignUid} successfully updated as fallback campaign`
    );
  };

  const handleUpdateFallback = (newFallbackCampaignUid: string) => {
    return updateFallback(
      newFallbackCampaignUid,
      `Successfully selected ${newFallbackCampaignUid} as fallback campaign`
    );
  };

  return (
    <RcsSectionBlock
      icon={faBullhorn}
      title="10DLC Campaign Fallback Details"
      showDivider={true}
      editable={false}
      headerAside={authorized ? <ActionButtons /> : null}
    >
      {fallbackCampaignUid ? (
        <InfoItemLayout.Vertical>
          <InfoItemLayout.Horizontal>
            <InfoItem title="10DLC Campaign ID">
              <Box
                role="link"
                sx={{ color: 't.blue100', cursor: 'pointer' }}
                onClick={() => {
                  history.push(`${MY_CAMPAIGNS_PATH}/${fallbackCampaignUid}`, {
                    goBackPage: `${location.pathname}${location.search}`,
                  });
                }}
              >
                {fallbackCampaignUid}
              </Box>
            </InfoItem>
            <InfoItem title="Use Case">{useCaseDisplayName}</InfoItem>
          </InfoItemLayout.Horizontal>
          <InfoItemLayout.Horizontal>
            <InfoItem title="Registered On">
              {convertTimeWithTimezone(
                fallbackCampaign?.createDate,
                undefined,
                'MM/DD/YYYY'
              )}
            </InfoItem>
            <InfoItem title="TCR Status">
              {!!fallbackCampaign?.status && (
                <StatusIndicator
                  variant={
                    fallbackCampaign?.status?.toLocaleLowerCase() === 'active'
                      ? 'success'
                      : 'error'
                  }
                />
              )}
              {remapTCRStatus(fallbackCampaign?.status)}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        </InfoItemLayout.Vertical>
      ) : (
        <Box sx={{ my: 'm', textAlign: 'center' }}>
          No fallback campaign found
        </Box>
      )}
      <TenDlcCampaignFallbackEditModal
        open={!!modalType}
        type={modalType}
        rcsCampaignUid={rcsCampaignUid}
        brandUid={brandUid}
        tenDlcUseCases={usecases}
        fallbackCampaignUid={fallbackCampaignUid}
        onClose={() => setModalType(null)}
        onRemove={handleRemoveFallback}
        onUpdate={handleUpdateFallback}
        onSelect={handleSelectFallback}
        loading={processing}
      />
    </RcsSectionBlock>
  );
};

export default TenDlcCampaignFallbackSection;
